import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useLocation } from "wouter";
import { Command } from "cmdk";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useCommandPalette } from "./command-palette-context";
import { useMe, useMyFeatures } from "@/lib/auth";
import {
  useListSales,
  getListSalesQueryKey,
  useListInventory,
  getListInventoryQueryKey,
  useListCustomers,
  getListCustomersQueryKey,
  useListTasks,
  getListTasksQueryKey,
  useListHrStaff,
  getListHrStaffQueryKey,
} from "@workspace/api-client-react";
import { useTabs } from "@/components/tabs/tabs-context";
import type { SidebarItemNode } from "@/lib/sidebar-config";
import { fuzzyMatch, getSidebarPageSearchResults } from "@/lib/sidebar-page-search";
import {
  type SearchResult,
  useOperationsSearch,
  useRecordSearchSelection,
} from "@/hooks/use-operations";

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const MAX_PER_CATEGORY = 5;

interface PaletteItem {
  id: string;
  label: string;
  subtitle?: string;
  emoji?: string;
  href?: string;
  action?: () => void;
}

function highlight(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[hsl(var(--secondary))]/30 text-inherit rounded-sm px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function PaletteRow({
  item,
  query,
  onSelect,
}: {
  item: PaletteItem;
  query: string;
  onSelect: (item: PaletteItem) => void;
}) {
  return (
    <Command.Item
      key={item.id}
      value={item.id + " " + item.label + " " + (item.subtitle ?? "")}
      onSelect={() => onSelect(item)}
      className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer aria-selected:bg-accent text-sm"
    >
      {item.emoji ? (
        <span className="text-base w-5 text-center flex-shrink-0" aria-hidden>
          {item.emoji}
        </span>
      ) : (
        <span className="w-5 flex-shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{highlight(item.label, query)}</div>
        {item.subtitle && (
          <div className="truncate text-xs text-muted-foreground">
            {highlight(item.subtitle, query)}
          </div>
        )}
      </div>
    </Command.Item>
  );
}

function QuickActions({ onSelect }: { onSelect: (item: PaletteItem) => void }) {
  const { has, isLoading } = useMe();
  const { hasFeature } = useMyFeatures();
  const { openTab } = useTabs();

  const actions = useMemo<PaletteItem[]>(() => {
    if (isLoading) return [];
    const list: PaletteItem[] = [];
    if (has("edit_sales")) {
      list.push({
        id: "qa-new-sale",
        label: "New Sale",
        subtitle: "Open the new sale form",
        emoji: "➕",
        action: () => openTab({ path: "/sales/new", title: "New Sale" }),
      });
    }
    if (has("edit_inventory")) {
      list.push({
        id: "qa-add-inventory",
        label: "Add Inventory Item",
        subtitle: "Create a new inventory item",
        emoji: "📦",
        action: () => openTab({ path: "/inventory/new", title: "New Inventory Item" }),
      });
    }
    if (has("tasks.create") && hasFeature("tasks_module_enabled")) {
      list.push({
        id: "qa-create-task",
        label: "Create Task",
        subtitle: "Open the task creation form",
        emoji: "✅",
        action: () => openTab({ path: "/tasks/new", title: "New Task" }),
      });
    }
    if (has("social.posts.compose")) {
      list.push({
        id: "qa-schedule-post",
        label: "Schedule Social Post",
        subtitle: "Open the post scheduler",
        emoji: "📣",
        action: () => openTab({ path: "/social/compose", title: "New Post" }),
      });
    }
    return list;
  }, [has, isLoading, openTab]);

  if (actions.length === 0) return null;
  return (
    <Command.Group heading="Quick Actions">
      {actions.map((item) => (
        <PaletteRow key={item.id} item={item} query="" onSelect={onSelect} />
      ))}
    </Command.Group>
  );
}

function PageResults({
  query,
  navItems,
  onSelect,
}: {
  query: string;
  navItems: SidebarItemNode[];
  onSelect: (item: PaletteItem) => void;
}) {
  const results = useMemo(
    () => getSidebarPageSearchResults(query, navItems),
    [query, navItems],
  );

  if (!query || results.length === 0) return null;
  return (
    <Command.Group heading="Pages">
      {results.map((item) => (
        <PaletteRow key={item.id} item={item} query={query} onSelect={onSelect} />
      ))}
    </Command.Group>
  );
}

function OrderResults({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (item: PaletteItem) => void;
}) {
  const { has } = useMe();
  const enabled = !!query && query.length >= 2 && has("view_sales");
  const { data } = useListSales(
    { search: query, limit: 20, offset: 0 },
    { query: { queryKey: getListSalesQueryKey({ search: query, limit: 20, offset: 0 }), enabled, staleTime: 10_000 } },
  );

  const results = useMemo<PaletteItem[]>(() => {
    if (!data?.items) return [];
    return data.items.slice(0, MAX_PER_CATEGORY).map((o) => ({
      id: "order-" + o.id,
      label: o.orderNumber,
      subtitle: [o.customerName, o.channel].filter(Boolean).join(" · "),
      emoji: "💰",
      href: `/sales/${o.id}`,
    }));
  }, [data]);

  if (!enabled || results.length === 0) return null;
  return (
    <Command.Group heading="Orders">
      {results.map((item) => (
        <PaletteRow key={item.id} item={item} query={query} onSelect={onSelect} />
      ))}
    </Command.Group>
  );
}

function CustomerResults({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (item: PaletteItem) => void;
}) {
  const enabled = !!query && query.length >= 2;
  const { data } = useListCustomers(
    { search: query },
    { query: { queryKey: getListCustomersQueryKey({ search: query }), enabled, staleTime: 10_000 } },
  );

  const results = useMemo<PaletteItem[]>(() => {
    const items = data?.items;
    if (!Array.isArray(items)) return [];
    return (items as Array<{ id: string; name: string; email?: string | null }>)
      .slice(0, MAX_PER_CATEGORY)
      .map((c) => ({
        id: "customer-" + c.id,
        label: c.name,
        subtitle: c.email ?? undefined,
        emoji: "👥",
        href: `/customers/${c.id}`,
      }));
  }, [data]);

  if (!enabled || results.length === 0) return null;
  return (
    <Command.Group heading="Customers">
      {results.map((item) => (
        <PaletteRow key={item.id} item={item} query={query} onSelect={onSelect} />
      ))}
    </Command.Group>
  );
}

function InventoryResults({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (item: PaletteItem) => void;
}) {
  const { has } = useMe();
  const enabled = !!query && query.length >= 2 && has("view_inventory");
  const { data } = useListInventory(
    { search: query },
    { query: { queryKey: getListInventoryQueryKey({ search: query }), enabled, staleTime: 10_000 } },
  );

  const results = useMemo<PaletteItem[]>(() => {
    if (!data?.items) return [];
    return data.items
      .slice(0, MAX_PER_CATEGORY)
      .map((inv) => ({
        id: "inv-" + inv.id,
        label: inv.productTitle,
        subtitle: inv.sku ?? undefined,
        emoji: "📦",
        href: `/inventory/${inv.id}`,
      }));
  }, [data]);

  if (!enabled || results.length === 0) return null;
  return (
    <Command.Group heading="Inventory">
      {results.map((item) => (
        <PaletteRow key={item.id} item={item} query={query} onSelect={onSelect} />
      ))}
    </Command.Group>
  );
}

function TaskResults({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (item: PaletteItem) => void;
}) {
  const { has } = useMe();
  const { hasFeature } = useMyFeatures();
  const canSearch = !!query && query.length >= 2 && hasFeature("tasks_module_enabled") && has("tasks.view");
  const { data } = useListTasks(
    { scope: "all", status: "open" },
    { query: { queryKey: getListTasksQueryKey({ scope: "all", status: "open" }), enabled: canSearch, staleTime: 30_000 } },
  );

  const results = useMemo<PaletteItem[]>(() => {
    if (!Array.isArray(data)) return [];
    return (data as Array<{ id: string; title: string; projectName?: string | null }>)
      .filter((t) => fuzzyMatch(t.title + " " + (t.projectName ?? ""), query))
      .slice(0, MAX_PER_CATEGORY)
      .map((t) => ({
        id: "task-" + t.id,
        label: t.title,
        subtitle: t.projectName ?? undefined,
        emoji: "✅",
        href: `/tasks/${t.id}`,
      }));
  }, [data, query]);

  if (!canSearch || results.length === 0) return null;
  return (
    <Command.Group heading="Tasks">
      {results.map((item) => (
        <PaletteRow key={item.id} item={item} query={query} onSelect={onSelect} />
      ))}
    </Command.Group>
  );
}

function StaffResults({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (item: PaletteItem) => void;
}) {
  const { has } = useMe();
  const { hasFeature } = useMyFeatures();
  const enabled = !!query && query.length >= 2 && hasFeature("hr_module_enabled") && has("hr.staff.view");
  const { data } = useListHrStaff(
    { q: query },
    { query: { queryKey: getListHrStaffQueryKey({ q: query }), enabled, staleTime: 10_000 } },
  );

  const results = useMemo<PaletteItem[]>(() => {
    if (!data?.items) return [];
    return data.items.slice(0, MAX_PER_CATEGORY).map((s) => ({
      id: "staff-" + s.id,
      label: `${s.firstName} ${s.lastName}`,
      subtitle: [s.jobTitle, s.department].filter(Boolean).join(" · ") || undefined,
      emoji: "👤",
      href: `/hr/staff/${s.id}`,
    }));
  }, [data]);

  if (!enabled || results.length === 0) return null;
  return (
    <Command.Group heading="Staff">
      {results.map((item) => (
        <PaletteRow key={item.id} item={item} query={query} onSelect={onSelect} />
      ))}
    </Command.Group>
  );
}

function operationsResultHref(result: SearchResult): string {
  const canonical = result.metadata?.canonicalHref;
  if (typeof canonical === "string" && canonical.startsWith("/")) return canonical;
  const routes: Record<string, string> = {
    products: `/inventory/${result.id}`,
    orders: `/sales/${result.id}`,
    customers: `/customers/${result.id}`,
    payments: "/sales",
    tracking: "/sales",
    boxes: "/commerce/boxes",
    purchasing: `/purchase-orders/${result.id}`,
    returns: `/returns/${result.id}`,
    consignments: `/consignment/payouts/${result.id}`,
    tasks: `/tasks/${result.id}`,
    exceptions: `/exceptions?focus=${result.id}`,
    tickets: "/account-portal/support",
    pre_orders: "/commerce/pre-orders",
    lots: "/inventory",
    suppliers: `/suppliers/${result.id}`,
    locations: "/warehouse/locations",
    staff: `/hr/staff/${result.id}`,
  };
  return routes[result.group] ?? "/operations/search";
}

function OperationsUniversalResults({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (item: PaletteItem) => void;
}) {
  const [, navigate] = useLocation();
  const { has } = useMe();
  const { hasFeature } = useMyFeatures();
  const allowed =
    has("operations.search.use")
    && hasFeature("operations_workspace" as never)
    && hasFeature("operations_universal_search" as never);
  const search = useOperationsSearch(allowed ? query : "", 20);
  const recordSelection = useRecordSearchSelection();
  const groups = Object.entries(search.data?.groups ?? {});

  if (!allowed || query.length < 2 || groups.length === 0) return null;
  return (
    <>
      {groups.map(([group, results]) => (
        <Command.Group key={group} heading={`Operations · ${group.replaceAll("_", " ")}`}>
          {results.slice(0, MAX_PER_CATEGORY).map((result) => {
            const item: PaletteItem = {
              id: `operations-${result.group}-${result.id}`,
              label: result.title,
              subtitle: String(result.metadata?.summary ?? result.entityType),
              action: () => {
                recordSelection.mutate({
                  entityType: result.entityType,
                  entityId: result.id,
                  query,
                  resultCount: search.data?.total ?? 0,
                });
                navigate(operationsResultHref(result));
              },
            };
            return <PaletteRow key={item.id} item={item} query={query} onSelect={onSelect} />;
          })}
        </Command.Group>
      ))}
    </>
  );
}

export function VCCommandPalette({ navItems }: { navItems: SidebarItemNode[] }) {
  const { open, setOpen } = useCommandPalette();
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 250);
  const inputRef = useRef<HTMLInputElement>(null);
  const [visualViewport, setVisualViewport] = useState(() => ({
    top: 0,
    left: 0,
    width: typeof window === "undefined" ? 1024 : window.innerWidth,
    height: typeof window === "undefined" ? 768 : window.innerHeight,
  }));

  useEffect(() => {
    const viewport = window.visualViewport;
    const update = () => {
      setVisualViewport({
        top: viewport?.offsetTop ?? 0,
        left: viewport?.offsetLeft ?? 0,
        width: viewport?.width ?? window.innerWidth,
        height: viewport?.height ?? window.innerHeight,
      });
    };
    update();
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSelect = useCallback(
    (item: PaletteItem) => {
      setOpen(false);
      if (item.action) {
        item.action();
      } else if (item.href) {
        navigate(item.href);
      }
    },
    [setOpen, navigate],
  );

  const isEmpty = !debouncedQuery;
  const isMac = typeof navigator !== "undefined" && navigator.platform?.includes("Mac");
  const dialogStyle: CSSProperties = {
    left: `${visualViewport.left + visualViewport.width / 2}px`,
    top: `calc(${visualViewport.top}px + env(safe-area-inset-top) + (var(--palette-visual-viewport-height) - env(safe-area-inset-top) - env(safe-area-inset-bottom)) / 2)`,
    width: `${Math.max(0, Math.min(672, visualViewport.width - 32))}px`,
    maxHeight: "calc(var(--palette-visual-viewport-height) - 2rem - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
    "--palette-visual-viewport-height": `${visualViewport.height}px`,
  } as CSSProperties;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="flex min-h-0 w-full max-w-2xl flex-col overflow-hidden p-0"
        style={dialogStyle}
        data-testid="command-palette-dialog"
        aria-describedby={undefined}
      >
        <Command
          loop
          className="flex min-h-0 flex-col [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground"
        >
          <div className="flex min-w-0 shrink-0 items-center gap-2 border-b px-3">
            <span className="text-muted-foreground text-base" aria-hidden>🔍</span>
            <Command.Input
              ref={inputRef}
              value={query}
              onValueChange={setQuery}
              placeholder="Search pages, orders, customers, inventory, tasks, staff…"
              className="min-w-0 flex-1 bg-transparent py-4 pr-10 text-base outline-none placeholder:text-muted-foreground sm:text-sm"
              data-testid="command-palette-input"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 text-[10px] text-muted-foreground font-mono select-none">
              Esc
            </kbd>
          </div>

          <Command.List className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2">
            <Command.Empty className="py-8 text-center text-sm text-muted-foreground">
              No results found.
            </Command.Empty>

            {isEmpty && <QuickActions onSelect={handleSelect} />}

            {!isEmpty && (
              <>
                <PageResults
                  query={debouncedQuery}
                  navItems={navItems}
                  onSelect={handleSelect}
                />
                <OperationsUniversalResults query={debouncedQuery} onSelect={handleSelect} />
                <OrderResults query={debouncedQuery} onSelect={handleSelect} />
                <CustomerResults query={debouncedQuery} onSelect={handleSelect} />
                <InventoryResults query={debouncedQuery} onSelect={handleSelect} />
                <TaskResults query={debouncedQuery} onSelect={handleSelect} />
                <StaffResults query={debouncedQuery} onSelect={handleSelect} />
              </>
            )}
          </Command.List>

          <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-t px-3 py-2 text-[11px] text-muted-foreground">
            <span><kbd className="font-mono">↑↓</kbd> navigate</span>
            <span><kbd className="font-mono">↵</kbd> select</span>
            <span><kbd className="font-mono">Esc</kbd> close</span>
            <span className="ml-auto">
              <kbd className="font-mono">{isMac ? "⌘K" : "Ctrl+K"}</kbd> to open
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
